import { isNil, omit } from "lodash-es";

import { ICodelist } from "@/data/codelists";
import { IJobRequestFilter, IJobRequestFilterDto, IJobRequestURLFilter } from "@/data/job-requests";
import { ILocation } from "@/data/locations";
import { ISkill } from "@/data/skills";

export const mapToJobRequestFilter = (jobRequestFilters: IJobRequestURLFilter): IJobRequestFilter => ({
  id: jobRequestFilters.id!,
  name: jobRequestFilters.name!,
  signature: jobRequestFilters.signature!,
  frequency: jobRequestFilters.frequency!,
  items: {
    filterType: "JOB_REQUEST",
    query: jobRequestFilters.query,
    location: Object.values(jobRequestFilters?.location ?? {}).some(k => !!k)
      ? {
          name: jobRequestFilters?.location?.name ?? "",
          country: jobRequestFilters?.location?.country ?? "",
          city: jobRequestFilters?.location?.city ?? "",
          radius: jobRequestFilters.radius,
          locationId: jobRequestFilters.location?.locationId,
          countryCode: jobRequestFilters.location?.countryCode,
        }
      : undefined,
    skillRoleCategories: jobRequestFilters?.skillRoleCategories ?? [],
    remote: jobRequestFilters.remote?.map(v => v.code),
    dedicated: jobRequestFilters.dedicated,
    originIds: jobRequestFilters.originIds ?? [],
    languages: jobRequestFilters.languages?.map(v => v.code),
    skillIds: jobRequestFilters.skillIds?.map(v => v.value),
    skills: jobRequestFilters.skills ?? [],
    level: jobRequestFilters.level?.map(v => v.code),
  },
});

export const mapToJobRequestFilterDto = (jobRequestFilter: IJobRequestFilter): IJobRequestFilterDto => ({
  ...jobRequestFilter,
  items: {
    ...jobRequestFilter.items,
    location: jobRequestFilter.items.location ? jobRequestFilter.items.location : ({} as ILocation),
  },
});

export const mapToJobRequestURLFilter = ({
  name,
  frequency,
  id,
  signature,
  items,
}: IJobRequestFilter): IJobRequestURLFilter => ({
  id,
  signature,
  name,
  frequency,
  location: Object.values({
    name: items?.location?.name,
    country: items?.location?.country,
    city: items?.location?.city,
    locationId: items.location?.locationId,
    countryCode: items.location?.countryCode,
  }).some(k => !!k)
    ? {
        name: items?.location?.name ?? "",
        country: items?.location?.country ?? "",
        city: items?.location?.city ?? null,
        locationId: items.location?.locationId,
        countryCode: items.location?.countryCode,
      }
    : undefined,
  radius: items.location?.radius ?? 0,
  remote: isNil(items.remote) ? [] : items.remote.map(v => ({ code: v, parameter: null })),
  skillRoleCategories: items?.skillRoleCategories ?? [],
  query: items?.query,
  dedicated: items?.dedicated ?? false,
  originIds: items?.originIds ?? [],
  favouritesOnly: items?.favouritesOnly ?? false,
  recommendedOnly: items?.recommendedOnly ?? false,
  languages: isNil(items.languages) ? [] : items.languages.map(v => ({ code: v, parameter: null })),
  skillIds: isNil(items.skillIds) ? [] : items.skillIds.map(v => ({ value: v, label: "" })),
  skills: isNil(items.skills) ? [] : items.skills,
  level: isNil(items.level) ? [] : items.level.map(v => ({ code: v, parameter: null })),
});

export const jobRequestFiltersToQueryMapper = {
  languages: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(o => o.code)),
  location: (v: ILocation, values: IJobRequestURLFilter) =>
    isNil(v) ? undefined : { ...omit(v, "name"), radius: values.radius },
  remote: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(o => o.code)),
  skillIds: (v: number[]) => (isNil(v) ? undefined : v),
  skills: (v: ISkill[]) => (isNil(v) ? undefined : v),
  query: (v: string) => (isNil(v) ? undefined : v),
  skillRoleCategoryIcon: (v: string[]) => (isNil(v) ? undefined : v),
  id: () => undefined,
  name: () => undefined,
  frequency: () => undefined,
  radius: () => undefined,
  signature: () => undefined,
  dedicated: (v: boolean) => (isNil(v) ? undefined : v),
  originIds: (v: number[]) => (isNil(v) ? undefined : v),
  favouritesOnly: (v: boolean) => (isNil(v) ? undefined : v),
  recommendedOnly: (v: boolean) => (isNil(v) ? undefined : v),
  level: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(o => o.code)),
};
